<template>
  <component
    :is="resolveLayoutVariant"
    :class="`skin-variant--${appSkinVariant}`"
  >
    <Snackbar />
    <transition
      :name="appRouteTransition"
      mode="out-in"
      appear
    >
      <router-view></router-view>
    </transition>
  </component>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import { computed, provide, onMounted } from '@vue/composition-api'
// eslint-disable-next-line import/no-unresolved
import useAppConfig from '@core/@app-config/useAppConfig'
import { useRouter } from '@core/utils'
import { useLayout } from '@core/layouts/composable/useLayout'

// Layouts
import LayoutContentVerticalNav from '@/layouts/variants/content/vertical-nav/LayoutContentVerticalNav.vue'
import LayoutBlank from '@/layouts/variants/blank/LayoutBlank.vue'
import Snackbar from '@/components/Snackbar.vue'

// Dynamic vh
import useDynamicVh from '@core/utils/useDynamicVh'

// Other libraries
import store from '@/store'
import { can } from '@core/libs/acl/utils'
import snackbarService from '@/services/snackbar.service'
import { useUtils } from '@core/libs/i18n'

export default {
  components: {
    LayoutContentVerticalNav,
    LayoutBlank,
    Snackbar,
  },
  setup() {
    provide('store', store)
    provide('snackbarService', snackbarService)
    provide('can', can)
    const { t } = useUtils()
    provide('t', t)

    const { route } = useRouter()
    const { appContentLayoutNav, appSkinVariant, appRouteTransition } = useAppConfig()

    const { handleBreakpointLayoutSwitch } = useLayout()
    handleBreakpointLayoutSwitch()

    const resolveLayoutVariant = computed(() => {
      if (route.value.meta.layout === 'blank') return 'layout-blank'
      if (route.value.meta.layout === 'content') return `layout-content-${appContentLayoutNav.value}-nav`

      return null
    })

    // Prefetch

    const fetchOrganization = () => {
      store
        .dispatch('fetchOrganization')
        .then(response => {
          const { data } = response.data
          store.dispatch('setOrganization', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching organizations. Please refresh!')
        })
    }
    const fetchBranches = () => {
      store
        .dispatch('fetchBranches')
        .then(response => {
          const { data } = response.data
          store.dispatch('setBranches', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching branches. Please refresh!')
        })
    }
    const fetchAccounts = () => {
      store
        .dispatch('fetchAccounts')
        .then(response => {
          const { data } = response.data
          store.dispatch('setAccounts', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching accounts. Please refresh!')
        })
    }
    const fetchInsuranceProducts = () => {
      store
        .dispatch('fetchInsuranceProducts')
        .then(response => {
          const { data } = response.data
          store.dispatch('setInsuranceProducts', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching insurance products. Please refresh!')
        })
    }
    const fetchInsurances = () => {
      store
        .dispatch('fetchInsurances')
        .then(response => {
          const { data } = response.data
          store.dispatch('setInsurances', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching insurances. Please refresh!')
        })
    }

    const fetchPendingRequests = () => {
      store
        .dispatch('fetchPendingRequests')
        .then(response => {
          const { data } = response.data
          store.dispatch('setPendingRequests', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching pending requests. Please refresh!')
        })
    }
    const fetchPendingSpd = () => {
      store
        .dispatch('fetchPendingSpd')
        .then(response => {
          const { data } = response.data
          store.dispatch('setPendingSpd', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching pending spd receipts. Please refresh!')
        })
    }
    const fetchPendingLeaveInvites = () => {
      store
        .dispatch('fetchPendingLeaveInvites')
        .then(response => {
          const { data } = response.data
          store.dispatch('setPendingLeaveInvites', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching pending leave invites. Please refresh!')
        })
    }
    const fetchPendingLeaveRequests = () => {
      store
        .dispatch('fetchPendingLeaveRequests')
        .then(response => {
          const { data } = response.data
          store.dispatch('setPendingLeaveRequests', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching pending leave requests. Please refresh!')
        })
    }

    const fetchPendingGmailNotifications = () => {
      store
        .dispatch('fetchPendingGmailNotifications')
        .then(response => {
          const { data } = response.data
          store.dispatch('setPendingGmailNotifications', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching pending gmail notifications. Please refresh!')
        })
    }

    /*
    const fetchLeadCounts = () => {
      store
        .dispatch('fetchLeadCounts')
        .then(response => {
          const { data } = response.data
          store.dispatch('setLeadCounts', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching lead counts. Please refresh!')
        })
    }

    const subscribeNotification = () => {
      appCable.subscriptions.create({
        channel: 'WebNotificationsChannel',
      },
      {
        connected() {
          if (firstMounted.value) {
            notifySubscription.perform('fetch_organization')
            notifySubscription.perform('fetch_branches')
            notifySubscription.perform('fetch_insurance_products')
            notifySubscription.perform('fetch_insurances')
            const userData = JSON.parse(localStorage.getItem('userData'))
            if (userData.role !== 'Runner') {
              notifySubscription.perform('fetch_pending_requests')
              notifySubscription.perform('fetch_pending_spd')
              notifySubscription.perform('fetch_pending_leave_invites')
              notifySubscription.perform('fetch_pending_leave_requests')
              notifySubscription.perform('fetch_pending_gmail_notifications')
            } else {
              notifySubscription.perform('fetch_accounts')
            }
            firstMounted.value = false
          }
          console.log('Connected to the web notification channel')
        },
        received(data) {
          if (data.status === 'success') {
            snackbarService.success(data.message)

            if (data.source === 'gmail') fetchPendingGmailNotifications()
            else if (data.source === 'lead') {
              fetchLeadCounts()

              if (data.event === 'new_lead' && (userTeam.value === 'Quotation Main Lead' || userTeam.value === 'Quotation Assistant')) newAudio.play().catch(err => console.log('Audio play failed', err))
              else if (data.event === 'quoted_lead' && userTeam.value === 'General Main Lead') quotedAudio.play().catch(err => console.log('Audio play failed', err))
            }
          } else if (data.detail) {
            snackbarService.error(`${data.message}///${data.detail.detail}`, 60000)
          } else if (data.organization) {
            store.dispatch('setOrganization', data.organization)
          } else if (data.branches) {
            store.dispatch('setBranches', data.branches)
          } else if (data.accounts) {
            store.dispatch('setAccounts', data.accounts)
          } else if (data.insurance_products) {
            store.dispatch('setInsuranceProducts', data.insurance_products)
          } else if (data.insurances) {
            store.dispatch('setInsurances', data.insurances)
          } else if (data.pending_requests) {
            store.dispatch('setPendingRequests', data.pending_requests)
          } else if (data.pending_spd) {
            store.dispatch('setPendingSpd', data.pending_spd)
          } else if (data.pending_leave_invites) {
            store.dispatch('setPendingLeaveInvites', data.pending_leave_invites)
          } else if (data.pending_leave_requests) {
            store.dispatch('setPendingLeaveRequests', data.pending_leave_requests)
          } else if (data.pending_gmail_notifications) {
            store.dispatch('setPendingGmailNotifications', data.pending_gmail_notifications)
          } else {
            snackbarService.error(data.message)
          }
        },
        disconnected() {
          console.log('Disconnected from web notification channel')
        },
      })
    }
    */

    /*
    watch(userStore, (newValue, oldValue) => {
      if (oldValue !== newValue) {
        subscribeNotification()
      }
    })
    */

    onMounted(() => {
      const userData = JSON.parse(localStorage.getItem('userData'))
      if (localStorage.getItem('userData') && localStorage.getItem('userAbility')) {
        store.dispatch('setUserTeam', { team: userData.team, access: userData.access, role: userData.role })
      }
      fetchOrganization()
      setTimeout(() => { fetchBranches() }, 200)
      setTimeout(() => { fetchAccounts() }, 400)
      setTimeout(() => { fetchInsuranceProducts() }, 600)
      setTimeout(() => { fetchInsurances() }, 800)
      if (userData.role !== 'Runner') {
        setTimeout(() => { fetchPendingRequests() }, 1000)
        setTimeout(() => { fetchPendingSpd() }, 1200)
        setTimeout(() => { fetchPendingLeaveInvites() }, 1400)
        setTimeout(() => { fetchPendingLeaveRequests() }, 1600)
        setTimeout(() => { fetchPendingGmailNotifications() }, 1800)
      }
    })

    useDynamicVh()

    return {
      resolveLayoutVariant,
      appSkinVariant,
      appRouteTransition,
    }
  },
}
</script>
